/**
 * utils/metricHelpers.js
 *
 * Helpers puros para resolver valores de métricas do Firestore.
 * Centraliza a lógica de resolução de tipo que antes estava espalhada
 * com o padrão `?.operand ?? value ?? 0` em 45+ lugares.
 */

/**
 * Resolve o valor numérico de uma métrica do Firestore.
 * O Firestore pode armazenar métricas como:
 *   - { operand: 150 }  (formato FieldValue.increment)
 *   - 150                (número direto)
 *   - undefined/null     (inexistente)
 *
 * @param {object|number|undefined} field - O campo da métrica
 * @param {number} fallback - Valor padrão se nulo/indefinido
 * @returns {number}
 */
export function val(field, fallback = 0) {
    if (field === null || field === undefined) return fallback;
    if (typeof field === "number") return field;
    if (typeof field === "object" && "operand" in field) return field.operand ?? fallback;
    return Number(field) || fallback;
}

/**
 * Acessa um campo aninhado e resolve seu valor.
 * Exemplo: pick(monthData, "sales.count") → resolve operand ou número
 *
 * @param {object} obj - Objeto raiz
 * @param {string} path - Caminho separado por ponto (ex: "members.renewals")
 * @param {number} fallback - Valor padrão
 * @returns {number}
 */
export function pick(obj, path, fallback = 0) {
    const parts = path.split(".");
    let current = obj;
    for (const part of parts) {
        if (current === null || current === undefined) return fallback;
        current = current[part];
    }
    return val(current, fallback);
}
